import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Table,
  CrudContext,
  Row,
  FormContext,
  FormSelect,
  MasterDetailForm,
  FormInput,
  ColumnProps,
  FormDatePicker,
  FormSwitch,
} from '@digital/ui';
import { Tooltip } from 'antd';
import { FormikProps } from 'formik';
import {
  AbaDadosGerais,
  downloadAnexoDemanda,
  excluiAnexo,
  getDadosGerais,
  updateInformacoesComplementares,
} from '@app/api/demanda';
import { baixarTemplatesDemanda } from '@app/api/template';
import UploadFile from '@app/components/upload';
import QuestionarioContextProvider from '../../../../../hooks/useQuestionario';
import ModalCreateDocumento from '../ModalCreateDocumento';
import ModalComentariosAnexoDemanda from '../ModalComentariosAnexoDemanda';
import ModalComentariosCampoDemanda from '../../analise/ModalComentariosCampoDemanda';
import './custom.css';
import * as Yup from 'yup';
import {optionsSwitch} from '@app/helpers/optionsSwitch';
import { EStatusDemanda } from '@app/enums/EStatusDemanda';
import { UploadComponent, Visibility } from '@app/components';

const Validacao = () => {
  const {
    id,
    templateContext,
    getTemplates,
    demandaData,
    update,
    setUpdate,
    statusCodigo,
    statusPrazo,
    statusPrazoExtenso,
    expectativaDePrazoConfirmacao,
    setExpectativaDePrazoConfirmacao,
    haveraMaoDeObraEmPTRConfirmacao,
    setHaveraMaoDeObraEmPTRConfirmacao,
    haveraSlaConfirmacao,
    setHaveraSlaConfirmacao,
    haveraDespesasReembolsaveisConfirmacao,
    setHaveraDespesasReembolsaveisConfirmacao,
    novoPrazoConfirmacao,
    setNovoPrazoConfirmacao,
    isVisitaTecnicaConfirmacao,
    setIsVisitaTecnicaConfirmacao,
  } = useContext(QuestionarioContextProvider);
  const [dadosGerais, setDadosGerais] = useState<AbaDadosGerais>();
  const [dadosInformacoesComplementares, setDadosInformacoesComplementares] = useState<any>({});

  useEffect(() => {
    async function fetchData() {
      await getTemplates(demandaData && demandaData.id);
    }
    fetchData();
  }, [demandaData, demandaData, update]);

  useEffect(() => {
    let isMounted = true;
    if (id) {
      getDadosGerais(id).then(data => {
        if (data && isMounted) {
          setDadosGerais(data);
        }
      });
    }
    return () => {
      isMounted = false;
    };
  }, [id, update]);

  useEffect(() => {
    if (dadosGerais) {
      setDadosInformacoesComplementares({
        ...dadosInformacoesComplementares,
        expectativaDePrazo: dadosGerais.expectativaDePrazo,
      });
    }
  }, [dadosGerais]);

  const handleSubmitInformacoes = (values: any) => {
    const submit = {};
    submit.id = id;
    submit.expectativaDePrazo = values.expectativaDePrazo;
    submit.haveraDespesasReembolsaveis = values.haveraDespesasReembolsaveis;
    submit.haveraMaoDeObraEmPTR = values.haveraMaoDeObraEmPTR;
    submit.haveraSla = values.haveraSla;
    submit.novoPrazo = values.novoPrazo;
    submit.isVisitaTecnica = values.isVisitaTecnica;

    submit.expectativaDePrazoStatus = values.expectativaDePrazoStatus;
    submit.haveraDespesasReembolsaveisStatus = values.haveraDespesasReembolsaveisStatus;
    submit.haveraMaoDeObraEmPTRStatus = values.haveraMaoDeObraEmPTRStatus;
    submit.haveraSlaStatus = values.haveraSlaStatus;
    submit.novoPrazoStatus = values.novoPrazoStatus;
    submit.isVisitaTecnicaStatus = values.isVisitaTecnicaStatus;
    updateInformacoesComplementares(submit).then(data => setUpdate(!update));
  };

  const handleExcluir = useCallback(
    async (id: string, obrigatorio: boolean) => {
      await excluiAnexo({ id, obrigatorio });
      setUpdate(!update);
      await getTemplates(demandaData && demandaData.id);
    },
    [getTemplates, demandaData],
  );

  const handleBaixarTemplates = () => {
    baixarTemplatesDemanda(demandaData.id);
  };

  const columnsInformacoesComplementares = (form: FormikProps<any>) => {
    const columns: ColumnProps<any>[] = [];
    columns.push({
      title: 'descrição',
      dataIndex: 'descricao',
    });

    columns.push({
      title: 'resposta',
      width: '15%',
      render: (text, record) => {
        if (dadosGerais) {
          if (record.id === 'expectativaDePrazo') {
            return (
              <>
                {record[record.id + 'Status']}
                <FormInput
                  id={`mrn-input-expectativaDePrazo-${record.id}`}
                  name={record.id}
                  onPressEnter={e => form.submitForm()}
                  required
                  validateSchema={Yup.number().required('Campo obrigatório.').typeError('Número inválido.')}
                  disabled={dadosGerais[record.id + 'Status'] !== 'inconsistente'}
                />
              </>
            );
          } else if (record.id === 'novoPrazo') {
            return (
              <FormDatePicker
                id={`mrn-datepicker-novoPrazo-${record.id}`}
                name={record.id}
                format="DD/MM/YYYY"
                onChange={e => {
                  form.setFieldValue('novoPrazo', e);
                  form.submitForm();
                }}
                required
                disabled={dadosGerais[record.id + 'Status'] !== 'inconsistente'}
              />
            );
          } else {
            return (
              <FormSelect
                id={`mrn-select-status-${record.id}`}
                options={optionsSwitch}
                name={record.id}
                onSelect={e => form.submitForm()}
                disabled={dadosGerais[record.id + 'Status'] !== 'inconsistente'}
              />
            );
          }
        }
        return <></>;
      },
    });

    columns.push({
      title: 'Comentários',
      width: '10%',
      dataIndex: 'contemComentarios',
      className: 'td-centralizado',
      render: (text, record) => (
        <ModalComentariosCampoDemanda
          idDoComponente='mrn-modal-ModalComentariosCampoDemanda'
          demandaId={id || ''}
          preSuprimentos={true}
          campoId={record.id} />
      ),
    });

    columns.push({
      title: 'Confirmação',
      width: '10%',
      dataIndex: 'confirmacao',
      className: 'td-centralizado',
      render: (text, record) => {
        switch (record.id) {
          case 'expectativaDePrazo':
            return (
              <FormSwitch
                name={'expectativaDePrazoConfirmacao'}
                id={`mrn-switch-expectativaDePrazoConfirmacao-${record.id}`}
                checked={expectativaDePrazoConfirmacao}
                onChange={e => setExpectativaDePrazoConfirmacao(e)}
                disabled={dadosGerais[record.id + 'Status'] === 'consistente'}
              />
            );
          case 'haveraMaoDeObraEmPTR':
            return (
              <FormSwitch
                name={'haveraMaoDeObraEmPTRConfirmacao'}
                id={`mrn-switch-haveraMaoDeObraEmPTRConfirmacao-${record.id}`}
                checked={haveraMaoDeObraEmPTRConfirmacao}
                onChange={e => setHaveraMaoDeObraEmPTRConfirmacao(e)}
                disabled={dadosGerais[record.id + 'Status'] === 'consistente'}
              />
            );
          case 'haveraSla':
            return (
              <FormSwitch
                name={'haveraSlaConfirmacao'}
                id={`mrn-switch-haveraSlaConfirmacao-${record.id}`}
                checked={haveraSlaConfirmacao}
                onChange={e => setHaveraSlaConfirmacao(e)}
                disabled={dadosGerais[record.id + 'Status'] === 'consistente'}
              />
            );
          case 'haveraDespesasReembolsaveis':
            return (
              <FormSwitch
                name={'haveraDespesasReembolsaveisConfirmacao'}
                id={`mrn-switch-haveraDespesasReembolsaveisConfirmacao-${record.id}`}
                checked={haveraDespesasReembolsaveisConfirmacao}
                onChange={e => setHaveraDespesasReembolsaveisConfirmacao(e)}
                disabled={dadosGerais[record.id + 'Status'] === 'consistente'}
              />
            );
          case 'novoPrazo':
            return (
              <FormSwitch
                name={'novoPrazoConfirmacao'}
                id={`mrn-switch-novoPrazoConfirmacao-${record.id}`}
                checked={novoPrazoConfirmacao}
                onChange={e => setNovoPrazoConfirmacao(e)}
                disabled={dadosGerais[record.id + 'Status'] === 'consistente'}
              />
            );
          case 'isVisitaTecnica':
            return (
              <FormSwitch
                name={'isVisitaTecnicaConfirmacao'}
                id={`mrn-switch-isVisitaTecnicaConfirmacao-${record.id}`}
                checked={isVisitaTecnicaConfirmacao}
                onChange={e => setIsVisitaTecnicaConfirmacao(e)}
                disabled={dadosGerais[record.id + 'Status'] === 'consistente'}
              />
            );
          default:
            break;
        }
      },
    });

    columns.push({
      title: 'Status',
      width: '15%',
      render: (text, record) => (
        <FormSelect
          name={record.id + 'Status'}
          onSelect={e => form.submitForm()}
          options={[
            { text: 'CONSISTENTE', value: 'consistente' },
            { text: 'INCONSISTENTE', value: 'inconsistente' },
          ]}
          disabled
        />
      ),
    });
    return columns;
  };

  const camposInformacoesComplementares = useMemo(() => {
    const campos = [];
    if (dadosGerais) {
      if (dadosGerais.tipo == 1) {
        campos.push({
          id: 'expectativaDePrazo',
          descricao: 'Expectativa de Prazo em meses',
          confirmado: false,
        });
        campos.push({
          id: 'haveraMaoDeObraEmPTR',
          descricao: 'Mão de Obra em Porto Trombetas',
          confirmado: false,
        });
        campos.push({
          id: 'haveraSla',
          descricao: 'SLA',
          confirmado: false,
        });
        campos.push({
          id: 'haveraDespesasReembolsaveis',
          descricao: 'Despesas Reembolsáveis',
          confirmado: false,
        });
        campos.push({
          id: 'isVisitaTecnica',
          descricao: 'Visita Técnica',
          confirmado: false,
        });
      }
      if (dadosGerais.tipo == 2 && dadosGerais.isPrazo) {
        campos.push({
          id: 'novoPrazo',
          descricao: 'Novo Prazo',
          confirmado: false,
        });
      }
    }
    return campos;
  }, [id, dadosGerais]);

  const columns = [
    {
      title: 'Anexos',
      dataIndex: 'descricaoDocumento',
    },
    {
      title: 'Ações',
      width: '25%',
      className: 'td-centralizado',
      render: (index, record) => (
        <>
          <Visibility
            visible={record && record.arquivoAtual && !record.aprovado}
          >
            <UploadComponent
              id={record.arquivos[0].id}
              idDoComponente={record.id}
              nomeIdFormulario={'Id'}
              enderecoApi={'anexoDemanda/Upload'}
              iconName="messaging-attach"
              retorno={async data => {
                if (data.valid) {
                  await getTemplates(demandaData && demandaData.id);
                }
              }}
              hasFile={record.arquivoAtual && record.arquivoAtual.local}
              label="Anexar"
              buttonClass="labeled"
            />
          </Visibility>

          <Visibility
            visible={record && record.arquivoAtual && record.arquivoAtual.local}>
            <Tooltip title="Baixar Arquivo">
              <Button
                id='mrn-button-anexo-download'
                style={{ marginLeft: '10px' }}
                icon="ui-download"
                type="link"
                onClick={() => {
                  downloadAnexoDemanda(record.arquivoAtual.id, record.arquivoAtual.nome);
                }}
                title="Baixar"
                className="labeled"
              />
            </Tooltip>

            <Visibility visible={record && !record.aprovado}>
              <Tooltip title="Excluir Anexo">
                <Button
                  id='mrn-button-anexo-excluir'
                  style={{ marginLeft: '10px' }}
                  type="link"
                  icon="science-minus"
                  onClick={() => {
                    handleExcluir(record.id, record.obrigatorio);
                  }}
                  disabled={statusCodigo === 7 || statusCodigo === 9 || statusCodigo === 2}
                  title="Excluir"
                  className="labeled"
                />
              </Tooltip>

            </Visibility>


          </Visibility>

        </>
      ),
    },
    {
      title: 'Comentários',
      dataIndex: 'historicoComentariosAnexoDemanda',
      width: '5%',
      className: 'td-centralizado',
      render: (index, record) => {
        return (
          <Tooltip title="Comentários">
            <ModalComentariosAnexoDemanda
              preSuprimentos={false}
              anexoId={record.id}
              contemComentario={record.historicoComentariosAnexoDemanda.length > 0}
            />
          </Tooltip>
        );
      },
    },
    {
      title: 'Status',
      width: '5%',
      render: (text, record) =>
        record.aprovado !== undefined ? (record.aprovado ? 'Validado' : 'Devolvido') : 'Pendente',
    },
  ];

  return (
    <>
      {statusCodigo === EStatusDemanda.AguardandoAnalisePreSuprimentos ? (
        <div style={{ textAlign: 'center' }}>
          <h3 style={{ color: 'rgb(105,105,105' }}
            id="mrn-label-aguardandoAnalisePreSuprimentos"
          >
            Sua documentação será validada e/ou comentada em até {statusPrazo} ({statusPrazoExtenso}) dias úteis.
          </h3>
        </div>
      ) : (
        <></>
      )}
      {statusCodigo === EStatusDemanda.AguardandoGestorTecnico ? (
        <div style={{ textAlign: 'center' }}>
          <h3 style={{ color: 'rgb(105,105,105' }}
            id="mrn-label-aguardandoGestorTecnico"
          >
            Sua Demanda será validada pelo Gestor Técnico em até {statusPrazo} ({statusPrazoExtenso}) dias úteis.
          </h3>
        </div>
      ) : (
        <></>
      )}
      {statusCodigo !== EStatusDemanda.AguardandoAnalisePreSuprimentos
        && statusCodigo !== EStatusDemanda.AguardandoGestorTecnico ? (
        <>
          <fieldset>
            <legend>Anexos</legend>
            <Row style={{ paddingLeft: '10px' }}>
              <ModalCreateDocumento getTemplates={getTemplates} />
              <Button
                style={{ borderRadius: '3px', marginBottom: '10px', marginLeft: '10px' }}
                type="primary"
                onClick={() => handleBaixarTemplates()}>
                Baixar Templates
              </Button>
            </Row>
            <Row style={{ paddingLeft: '10px', paddingRight: '5px' }}>
              <Table
                id="mrn-table-validacao-anexos"
                rowKey="id" columns={columns} dataSource={templateContext || []} pagination={false} />
            </Row>
          </fieldset>
          <fieldset>
            <legend>Informações Complementares</legend>
            <Row style={{ paddingLeft: '10px', paddingRight: '5px' }}>
              <MasterDetailForm initialValues={dadosGerais} onSubmit={handleSubmitInformacoes}>
                <FormContext.Consumer>
                  {({ formProps }) => (
                    <Table
                      id="mrn-table-validacao-informacoes-complementares"
                      columns={columnsInformacoesComplementares(formProps)}
                      dataSource={camposInformacoesComplementares}
                      rowKey="id"
                    />
                  )}
                </FormContext.Consumer>
              </MasterDetailForm>
            </Row>
          </fieldset>
        </>
      ) : (
        <></>
      )}
    </>
  );
};

export default Validacao;
